import { z } from "zod";

/** Unit type shown on a line. */
export enum ExtraUnitType {
  UNSPECIFIED = "UNSPECIFIED",
  PER_STAY = "PER_STAY",
  PER_NIGHT = "PER_NIGHT",
}

/** Rate type shown instead of an amount when the price is not constant. */
export enum ExtraRateType {
  UNSPECIFIED = "UNSPECIFIED",
  PERCENTAGE = "PERCENTAGE",
  FORMULA = "FORMULA",
  OWN = "OWN",
  SHARED = "SHARED",
}

const CategoryExtraPriceSchema = z.object({
  constantAmount: z.number().optional(),
  rateType: z.enum(ExtraRateType).optional(),
});

/** Price of a line: either a constant amount or a rate type label, never both. */
export class CategoryExtraPrice {
  public readonly constantAmount?: number;
  public readonly rateType?: ExtraRateType;

  private constructor(props: z.infer<typeof CategoryExtraPriceSchema>) {
    this.constantAmount = props.constantAmount;
    this.rateType = props.rateType;
  }

  static constant(amount: number): CategoryExtraPrice {
    return new CategoryExtraPrice(CategoryExtraPriceSchema.parse({ constantAmount: amount }));
  }

  static rate(rateType: ExtraRateType): CategoryExtraPrice {
    return new CategoryExtraPrice(CategoryExtraPriceSchema.parse({ rateType }));
  }

  static none(): CategoryExtraPrice {
    return new CategoryExtraPrice({});
  }

  isConstant(): boolean {
    return this.constantAmount !== undefined;
  }

  hasRateType(): boolean {
    return this.rateType !== undefined;
  }
}

const CategoryExtraLineSchema = z.object({
  extraId: z.string(),
  ticker: z.string().min(1),
  name: z.string(),
  unitType: z.enum(ExtraUnitType).default(ExtraUnitType.UNSPECIFIED),
  price: z
    .custom<CategoryExtraPrice>(value => value instanceof CategoryExtraPrice)
    .default(() => CategoryExtraPrice.none()),
  active: z.boolean(),
  coversInventoryLine: z.boolean(),
});

export type CategoryExtraLineProps = z.input<typeof CategoryExtraLineSchema>;

/**
 * One extra of a category block, as computed by the backend. Read-only:
 * whether it is ticked is state of the block, `CategoryLimit`.
 */
export class CategoryExtraLine {
  public readonly extraId: string;
  public readonly ticker: string;
  public readonly name: string;
  public readonly unitType: ExtraUnitType;
  public readonly price: CategoryExtraPrice;
  public readonly active: boolean;
  public readonly coversInventoryLine: boolean;

  private constructor(props: z.infer<typeof CategoryExtraLineSchema>) {
    this.extraId = props.extraId;
    this.ticker = props.ticker;
    this.name = props.name;
    this.unitType = props.unitType;
    this.price = props.price;
    this.active = props.active;
    this.coversInventoryLine = props.coversInventoryLine;
  }

  static of(props: CategoryExtraLineProps): CategoryExtraLine {
    return new CategoryExtraLine(CategoryExtraLineSchema.parse(props));
  }

  /** Inactive extras cannot be ticked. */
  isTickable(): boolean {
    return this.active;
  }

  /** Sellable = ticked, active and applying to an inventory line of the accommodation. */
  isSellableWhenTicked(): boolean {
    return this.active && this.coversInventoryLine;
  }
}
